import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { ImageUploadFolder } from '@/types';
import { imageApi } from '@/services/api';
import { Button } from '@/components/ui/Button';

interface UploadedImageValue {
  key: string;
  url: string;
}

interface ImageUploadFieldProps {
  label: string;
  folder: ImageUploadFolder;
  imageKey: string;
  previewUrl?: string;
  error?: string;
  helperText?: string;
  onChange: (value: UploadedImageValue) => void;
}

export const ImageUploadField = ({
  label,
  folder,
  imageKey,
  previewUrl = '',
  error,
  helperText,
  onChange,
}: ImageUploadFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [localPreviewUrl, setLocalPreviewUrl] = useState(previewUrl);

  useEffect(() => {
    setLocalPreviewUrl(previewUrl);
  }, [previewUrl]);

  const handleSelectFile = () => {
    inputRef.current?.click();
  };

  const handleRemove = () => {
    setUploadError('');
    setLocalPreviewUrl('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    onChange({ key: '', url: '' });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadError('');
    setIsUploading(true);

    try {
      const uploaded = await imageApi.uploadImage(file, folder);
      const signedUrl = await imageApi.getSignedUrl(uploaded.key);
      setLocalPreviewUrl(signedUrl);
      onChange({ key: uploaded.key, url: signedUrl });
    } catch (uploadFailure) {
      setUploadError((uploadFailure as Error).message || 'Image upload failed.');
    } finally {
      setIsUploading(false);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    }
  };

  return (
    <div className="w-full">
      <label className="form-label">{label}</label>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start">
          <div className="flex h-32 w-full items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-50 sm:w-40">
            {localPreviewUrl ? (
              <img src={localPreviewUrl} alt={label} className="h-full w-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 text-center text-sm text-slate-500">
                <ImagePlus className="h-6 w-6" />
                <span>Upload preview</span>
              </div>
            )}
          </div>

          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap gap-3">
              <Button type="button" variant="secondary" onClick={handleSelectFile} disabled={isUploading}>
                {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                {isUploading ? 'Uploading...' : 'Choose Image'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                onClick={handleRemove}
                disabled={isUploading || (!imageKey && !localPreviewUrl)}
              >
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            </div>

            <p className="text-xs text-slate-500">Allowed: JPG, PNG, WEBP. Max size: 5 MB.</p>
            {helperText ? <p className="text-sm text-slate-600">{helperText}</p> : null}
            {imageKey ? (
              <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
                <span className="font-semibold text-slate-700">Stored key:</span> {imageKey}
              </div>
            ) : null}
            {uploadError ? <p className="text-sm font-medium text-red-600">{uploadError}</p> : null}
            {error ? <p className="text-sm font-medium text-red-600">{error}</p> : null}
          </div>
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
};
